import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import { memo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ServiceItem } from './ServiceItem';
import { ROUTE_KEYS, USDColor } from 'legacyfrontend/constants';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useFlightContext } from 'legacyfrontend/contexts/FlightDetailsContext';
import { CARD_WIDTH } from '.';
import { formatPrice } from 'legacyfrontend/helpers/formatPrice';
import { updateFlightSearchState } from 'legacyfrontend/helpers/flightSearchStateStorage';
import { getCacheStatus } from 'legacyfrontend/helpers/getCacheStatus';

export const FareCard = memo(({ fare, baseFare, background, gray, text, disabled, currency }) => {
  const { selectFare, flightInfo } = useFlightContext();
  const isBaseFare = fare.id === baseFare?.id;
  const priceDifference = !isBaseFare ? fare.total - baseFare.total : 0;

  const buildSearchUrlFromFlightInfo = useCallback(() => {
    const originId = flightInfo?.originId || flightInfo?.origin?.cityCode;
    const originName = flightInfo?.originName || flightInfo?.origin?.cityName;
    const destinationId = flightInfo?.destinationId || flightInfo?.destination?.cityCode;
    const destinationName = flightInfo?.destinationName || flightInfo?.destination?.cityName;
    
    const hasFlightInfo = flightInfo && 
      originId && 
      originName &&
      destinationId && 
      destinationName &&
      flightInfo.departureDate;

    if (!hasFlightInfo) {
      const currentUrl = typeof window !== 'undefined' ? window.location.search : '';
      return currentUrl;
    }

    const params = new URLSearchParams();

    if (flightInfo.departureDate) {
      params.set('departureDate', flightInfo.departureDate);
    }

    if (flightInfo.returnDate && !flightInfo.isOnlyOneWay) {
      params.set('returnDate', flightInfo.returnDate);
    }

    if (originId && originName) {
      params.set('originId', originId);
      params.set('originName', originName);
      const originIdn = flightInfo.origin?.id;
      if (originIdn && !isNaN(originIdn)) {
        params.set('originIdn', originIdn);
      }
    }

    if (destinationId && destinationName) {
      params.set('destinationId', destinationId);
      params.set('destinationName', destinationName);
      const destinationIdn = flightInfo.destination?.id;
      if (destinationIdn && !isNaN(destinationIdn)) {
        params.set('destinationIdn', destinationIdn);
      }
    }

    if (flightInfo.passengers && Array.isArray(flightInfo.passengers)) {
      params.set('passengers', flightInfo.passengers.join(','));
    } else {
      params.set('passengers', '30');
    }

    const isOnlyOneWay = flightInfo.isOnlyOneWay ?? false;
    params.set('isOnlyOneWay', isOnlyOneWay ? 'true' : 'false');

    if (flightInfo.currency) {
      params.set('currency', flightInfo.currency);
    }

    params.set('cache', getCacheStatus() ? 'true' : 'false');
    params.set('_refresh', Date.now());

    const builtUrl = `?${params.toString()}`;

    return builtUrl;
  }, [flightInfo]);

  const handleSaveCurrentUrl = useCallback(() => {
    try {
      const searchParams = buildSearchUrlFromFlightInfo();
      updateFlightSearchState({ previousSearchUrl: searchParams });
    } catch (error) {
      console.warn('Unable to save previous URL to flightSearchState', error);
    }
  }, [buildSearchUrlFromFlightInfo]);

  const handleReservar = () => {
    selectFare(fare);
    handleSaveCurrentUrl();
  };

  // Avoid filter/opacity on hover: with overflow:hidden ancestors they can promote a compositor
  // layer and cause a visible vertical jump. Keep hover visually identical to the rest state.
  const reservarButtonSx = {
    boxShadow: 'none',
    textDecoration: 'none',
    '&:hover': {
      boxShadow: 'none',
      backgroundColor: background,
      color: text,
      textDecoration: 'none',
    },
    '&:active': {
      boxShadow: 'none',
      backgroundColor: background,
      color: text,
    },
  };

  return (
    <Card
      className='card'
      sx={{
        border: isBaseFare ? 2 : 1,
        borderColor: isBaseFare ? background : gray,
        width: { xs: '310px', sm: `${CARD_WIDTH}px` },
        minHeight: { xs: '500px', sm: '560px' },
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
      }}
      variant='outlined'
    >
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          p: { xs: 2.5, sm: 3 },
          '&:last-child': { pb: { xs: 2.5, sm: 3 } },
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Typography
            sx={{ fontSize: { xs: '2rem', sm: '2.35rem' }, lineHeight: 1.05, wordBreak: 'break-word' }}
            title={formatPrice(fare.total, currency, true)}
            variant='h4'
          >
            {formatPrice(fare.total, currency, true)}
          </Typography>

          {!isBaseFare ? (
            <Typography sx={{ mb: 3, color: USDColor, minHeight: '2.75rem' }} variant='subtitle2'>
              {`${priceDifference > 0 ? `+ ${formatPrice(priceDifference, currency, true)}` : `- ${formatPrice(priceDifference * -1, currency, true)}`} sobre tarifa base`}
            </Typography>
          ) : (
            <Typography sx={{ mb: 3, color: '#000', fontWeight: 800, minHeight: '2.75rem' }} variant='subtitle2'>
              Tarifa aplicada
            </Typography>
          )}

          {disabled ? (
            <Button
              disableElevation
              disableRipple
              disabled
              fullWidth
              sx={{
                mb: 3,
                backgroundColor: background,
                color: text,
                boxShadow: 'none',
                py: 1.25,
                '&.MuiButton-containedPrimary': {
                  backgroundColor: background,
                  color: text,
                },
              }}
              variant='contained'
            >
              Reservar
            </Button>
          ) : (
            <Button
              component={Link}
              disableElevation
              disableRipple
              fullWidth
              onClick={handleReservar}
              sx={{
                mb: 3,
                backgroundColor: background,
                color: text,
                py: 1.25,
                ...reservarButtonSx,
                '&.MuiButton-containedPrimary': {
                  backgroundColor: background,
                  color: text,
                },
              }}
              to={ROUTE_KEYS.BUSCAR_X_VUELOS_RESERVA}
              variant='contained'
            >
              Reservar
            </Button>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 2, sm: 2.5 },
            marginTop: 3,
            flex: 1,
            minHeight: 0,
          }}
        >
          {fare.services.map((service, index) => (
            <ServiceItem
              journeys={fare.journeys}
              key={`${service.code}-${index}`}
              service={service}
            />
          ))}
        </Box>

        {!!fare?.families[0] && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              flexShrink: 0,
              mt: 3,
              borderTop: `1px solid ${gray}`,
              pt: 2.5,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'text.secondary',
              }}
            >
              <ArrowForwardIcon fontSize='small' />
              <Typography variant='body2'>{fare?.families[0]}</Typography>
            </Box>
            {!!fare?.families[1] && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'text.secondary',
                }}
              >
                <ArrowBackIcon fontSize='small' />
                <Typography variant='body2'>{fare?.families[1]}</Typography>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
});
